import { Editor, Transforms, Element as SlateElement, Text } from 'slate';
import Link from '../Elements/Link/Link';
import Image from '../Elements/Embed/Image';
import Table from '../Elements/Table/Table';
import TableWithoutHeader from '../Elements/Table/TableWithoutHeaders';
import editorStyles from '../TextEditor.module.scss';
import tableStyles from '../Elements/Table/TableSelector.module.scss';
import TableHeader from '../Elements/Table/components/TableHeader/TableHeader';
import TableCell from '../Elements/Table/components/TableCell/TableCell';
import {
    applyFormatToMultipleSelectedColumns,
    applyFormatToMultipleSelectedRows,
    isFormatActiveInMultipleSelectedColumns,
    isFormatActiveInMultipleSelectedRows,
    isMultipleColumnsAlignmentActive,
    isMultipleRowsAlignmentActive,
    toggleMultipleColumnsAlignment,
    toggleMultipleColumnsFormat,
    toggleMultipleRowsAlignment,
    toggleMultipleRowsFormat,
} from './tableRowColumnSelections';

const alignment = ['left', 'right', 'center'];
const list_types = ['orderedList', 'unorderedList'];

export const sizeMap = {
    small: '0.75em',
    normal: '1em',
    medium: '1.75em',
    huge: '2.5em',
};
export const fontFamilyMap = {
    sans: 'Helvetica,Arial, sans serif',
    serif: 'Georgia, Times New Roaman,serif',
    monospace: 'Monaco, Courier New,monospace',
    pacifico: 'Pacifico, cursive',
};
export const toggleBlock = (editor, format) => {
    if (editor.selectedRows?.length > 0 && alignment.includes(format)) {
        toggleMultipleRowsAlignment(editor, format);
        return;
    }
    if (editor.selectedColumns?.length > 0 && alignment.includes(format)) {
        toggleMultipleColumnsAlignment(editor, format);
        return;
    }
    const isActive = isBlockActive(editor, format);
    const isList = list_types.includes(format);
    const isAlignment = alignment.includes(format);

    if (isAlignment) {
        Transforms.setNodes(editor, { textAlign: format }, { match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) });
    } else if (isList) {
        Transforms.unwrapNodes(editor, {
            match: (n) => list_types.includes(!Editor.isEditor(n) && SlateElement.isElement(n) && n.type),
            split: true,
        });
        Transforms.setNodes(
            editor,
            { type: isActive ? 'paragraph' : 'list-item' },
            { match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) },
        );
        if (!isActive) {
            Transforms.wrapNodes(editor, {
                type: format,
                children: [],
            });
        }
    } else {
        Transforms.setNodes(
            editor,
            { type: isActive ? 'paragraph' : format },
            { match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) },
        );
    }
};

export const addMarkData = (editor, data) => {
    if (editor.selectedRows?.length > 0) {
        applyFormatToMultipleSelectedRows(editor, data.format, data.value);
        return;
    }
    if (editor.selectedColumns?.length > 0) {
        applyFormatToMultipleSelectedColumns(editor, data.format, data.value);
        return;
    }
    Editor.addMark(editor, data.format, data.value);
};

export const toggleMark = (editor, format) => {
    if (editor.selectedRows?.length > 0) {
        toggleMultipleRowsFormat(editor, format);
        return;
    }
    if (editor.selectedColumns?.length > 0) {
        toggleMultipleColumnsFormat(editor, format);
        return;
    }
    const isActive = isMarkActive(editor, format);

    if (isActive) {
        Editor.removeMark(editor, format);
    } else {
        Editor.addMark(editor, format, true);
    }
};

export const isMarkActive = (editor, format) => {
    if (editor.selectedRows?.length > 0) {
        return isFormatActiveInMultipleSelectedRows(editor, format);
    }
    if (editor.selectedColumns?.length > 0) {
        return isFormatActiveInMultipleSelectedColumns(editor, format);
    }
    const marks = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};

export const isBlockActive = (editor, format) => {
    if (editor.selectedRows?.length > 0 && alignment.includes(format)) {
        return isMultipleRowsAlignmentActive(editor, format);
    }
    if (editor.selectedColumns?.length > 0 && alignment.includes(format)) {
        return isMultipleColumnsAlignmentActive(editor, format);
    }
    const [match] = Editor.nodes(editor, {
        match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
    });

    return !!match;
};

//     const { tablePath, columnIndex } = editor.selectedColumn;

//     try {
//         const [tableNode] = Editor.node(editor, tablePath);

//         if (!SlateElement.isElement(tableNode)) return false;

//         let hasAlignment = false;

//         // Check alignment in all cells of the column
//         tableNode.children.forEach((row, rowIndex) => {
//             if (SlateElement.isElement(row) && columnIndex < row.children.length) {
//                 const cellPath = [...tablePath, rowIndex, columnIndex];
//                 const [cellNode] = Editor.node(editor, cellPath);

//                 if (SlateElement.isElement(cellNode) && cellNode.textAlign === format) {
//                     hasAlignment = true;
//                 }
//             }
//         });

//         return hasAlignment;
//     } catch (error) {
//         console.error('Error checking alignment in column:', error);
//         return false;
//     }
// };
export const activeMark = (editor, format) => {
    const defaultMarkData = {
        color: 'black',
        bgColor: 'black',
        fontSize: '12px',
        fontFamily: 'sans',
        lineHeight: '130%',
    };
    const marks = Editor.marks(editor);
    const defaultValue = defaultMarkData[format];
    return marks?.[format] !== '#ffffff' ? marks?.[format] : defaultValue;
};

export const getMarked = (leaf, children) => {
    let styledChildren = children;

    // Apply all formatting in a composable way
    if (leaf.bold) {
        styledChildren = <strong>{styledChildren}</strong>;
    }

    if (leaf.code) {
        styledChildren = <code>{styledChildren}</code>;
    }

    if (leaf.italic) {
        styledChildren = <em>{styledChildren}</em>;
    }

    if (leaf.strikethrough) {
        // Use simple text-decoration for PDF compatibility
        styledChildren = (
            <s
                style={{
                    textDecoration: 'line-through',
                    textDecorationColor: leaf.color || 'currentColor',
                }}
            >
                {styledChildren}
            </s>
        );
    }

    if (leaf.underline) {
        styledChildren = (
            <u
                style={{
                    textDecoration: 'underline',
                    textDecorationColor: leaf.color || 'currentColor',
                }}
            >
                {styledChildren}
            </u>
        );
    }

    if (leaf.superscript) {
        styledChildren = <sup>{styledChildren}</sup>;
    }

    if (leaf.subscript) {
        styledChildren = <sub>{styledChildren}</sub>;
    }

    // Combine color, background, font styles into a single span to reduce nesting
    const inlineStyles = {};
    if (leaf.color) {
        inlineStyles.color = leaf.color;
    }
    if (leaf.bgColor) {
        inlineStyles.backgroundColor = leaf.bgColor;
    }
    if (leaf.fontSize) {
        inlineStyles.fontSize = leaf.fontSize;
    }
    if (leaf.fontFamily) {
        const family = fontFamilyMap[leaf.fontFamily];
        inlineStyles.fontFamily = family;
    }

    // Apply inline styles if any exist
    return (
        <span
            style={Object.keys(inlineStyles).length > 0 ? inlineStyles : {}}
            data-slate-marks={Object.keys(leaf)
                .filter((k) => k !== 'text')
                .join(',')}
        >
            {styledChildren}
        </span>
    );
};

export const getBlock = (props) => {
    const { element, children } = props;
    const attributes = props.attributes ?? {};
    const style = { textAlign: element.textAlign };

    switch (element.type) {
        case 'headingOne':
            return (
                <h1 style={style} {...attributes} {...element.attr}>
                    {children}
                </h1>
            );
        case 'headingTwo':
            return (
                <h2 style={style} {...attributes} {...element.attr}>
                    {children}
                </h2>
            );
        case 'headingThree':
            return (
                <h3 style={style} {...attributes} {...element.attr}>
                    {children}
                </h3>
            );
        case 'blockquote':
            return (
                <blockquote style={style} {...attributes} {...element.attr}>
                    {children}
                </blockquote>
            );
        case 'list-item':
            return (
                <li style={style} {...attributes} {...element.attr}>
                    {children}
                </li>
            );
        case 'orderedList':
            return (
                <ol
                    type="1"
                    style={{
                        ...style,
                        paddingLeft: '24px',
                        listStyleType: 'decimal',
                        marginBottom: '8px',
                    }}
                    {...attributes}
                    className="dontbreak"
                >
                    {children}
                </ol>
            );
        case 'unorderedList':
            return (
                <ul
                    style={{
                        ...style,
                        paddingLeft: '24px',
                        listStyleType: 'disc',
                        marginBottom: '8px',
                    }}
                    {...attributes}
                >
                    {children}
                </ul>
            );
        case 'link':
            return <Link {...props} />;
        case 'table':
            return <Table {...props} />;
        case 'tableWithoutHeaders':
            return <TableWithoutHeader {...props} />;
        case 'table-cell':
            return <TableCell {...props} />;
        case 'table-header':
            return <TableHeader {...props} {...element.attr} style={style} />;
        case 'table-row':
            return (
                <tr style={style} {...attributes} className={tableStyles.TableRow}>
                    {children}
                </tr>
            );
        case 'image':
            return <Image {...props} />;
        // case 'htmlCode':
        //     return <HtmlCode {...props} />;
        default:
            return (
                <div style={{ ...(style || {}) }} {...element.attr} {...attributes}>
                    {children}
                </div>
            );
    }
};
